import { Checkbox, makeStyles } from '@fluentui/react-components';

import { TItem } from '@modules/items';
import { THeading } from '@modules/documents';
import { BaseCard, SmartTableCell } from '@shared/components';
import { buildHref } from '@shared/utils';

const useStyles = makeStyles({
  headingCheckbox: {
    height: '24px',
    '& div': {
      margin: '4px',
    },
  },
});

type TImpactCardSelection = {
  item: boolean; // if the entire item is selected
  headingIds: string[];
  onSelect: (itemId: string, headingId?: string) => void;
  onDeselect: (itemId: string, headingId?: string) => void;
};

export type TImpactItem = {
  item: TItem;
  headings?: THeading[];
};

type TProps = TImpactItem & {
  selection?: TImpactCardSelection;
};

/**
 * A card that shows the impacted item, plus headings if relevant.
 * Reuses BaseCard for styling. Does not use selection.
 */
const ImpactCard = ({ item, headings, selection }: TProps) => {
  const isItemSelected = !selection || (selection.item && !headings) || selection.headingIds.length > 0;

  const classes = useStyles();

  return (
    <BaseCard selectable={false}>
      <div className="flex items-center gap-2" style={{ opacity: isItemSelected ? 1 : 0.6 }}>
        <SmartTableCell
          wrap="layout-only"
          variant={{ kind: 'item', item, elem: headings?.[0]?.id }}
          tooltip={{ threshold: 21 }}
          linkBold
        />
      </div>

      {/* Headings or entire file */}
      <div className="pl-1">
        {headings ? (
          headings.map((heading) => {
            const isChecked = !!selection && selection.headingIds.includes(heading.id);
            return (
              <div key={heading.id} className="flex items-center gap-2">
                {selection && (
                  <Checkbox
                    checked={isChecked}
                    onChange={(e) =>
                      e.target.checked
                        ? selection.onSelect(item._id, heading.id)
                        : selection.onDeselect(item._id, heading.id)
                    }
                    className={classes.headingCheckbox}
                  />
                )}
                <SmartTableCell
                  wrap="layout-only"
                  variant={{
                    kind: 'link',
                    label: `${heading.numbering} ${heading.text}`,
                    href: buildHref(`/${item.type.toLocaleLowerCase()}/${item._id}`, { elem: heading.id }),
                  }}
                />
              </div>
            );
          })
        ) : selection ? (
          <Checkbox
            label="Entire file"
            checked={selection.item}
            onChange={(e) => (e.target.checked ? selection.onSelect(item._id) : selection.onDeselect(item._id))}
            className={classes.headingCheckbox}
          />
        ) : (
          <p>Entire file</p>
        )}
      </div>
    </BaseCard>
  );
};

export default ImpactCard;
